import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { capitalizeEachWord } from "../../../utils/capitalize";
import { toDow } from "../../../utils/day-of-week";
import type { IntervalJson } from "../../../utils/interval";
import {
  type INTERVAL,
  type ISO8601_DATE,
  type ISO8601_TIME,
  formatDatetime,
  toISO8601_DATE,
} from "../../../utils/iso8601";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";
import { isSurveyCompletedKey } from "../../survey/utils/survey-event-key";
import { type ScheduleWhen, SupabaseScheduleService } from "../server/supabase-schedule-service";

export const NextSendsWrapper: FC = () => {
  const { control, getValues } = useFormContext<MomentFormMoment>();

  useWatch({
    control,
    name: [
      "immediate",
      "timezone",
      "schedule.event.calendar",
      "schedule.event.title",
      "schedule.event.number_of_repeats",
      "schedule.event.date",
      "schedule.event.key",
      "schedule.event_initial",
      "schedule.event_repeat",
      "schedule.interval_json",
      "schedule.on_event_key",
      "schedule.send_at",
      "trigger_survey_id",
    ],
  });

  const [
    immediate,
    timezone,
    event_calendar,
    event_title,
    event_number_of_repeats,
    event_repeat,
    event_date,
    event_key,
    schedule_event_initial,
    schedule_event_repeat,
    schedule_interval_json,
    schedule_on_event_key,
    schedule_send_at,
    trigger_survey_id,
  ] = getValues([
    "immediate",
    "timezone",
    "schedule.event.calendar",
    "schedule.event.title",
    "schedule.event.number_of_repeats",
    "schedule.event.repeat",
    "schedule.event.date",
    "schedule.event.key",
    "schedule.event_initial",
    "schedule.event_repeat",
    "schedule.interval_json",
    "schedule.on_event_key",
    "schedule.send_at",
    "trigger_survey_id",
  ]);

  return (
    <NextSends
      immediate={immediate}
      timezone={timezone}
      event_calendar={event_calendar}
      event_title={event_title}
      event_number_of_repeats={event_number_of_repeats}
      event_repeat={event_repeat}
      event_date={event_date}
      event_key={event_key}
      schedule_event_initial={schedule_event_initial}
      schedule_event_repeat={schedule_event_repeat}
      schedule_interval_json={schedule_interval_json}
      schedule_on_event_key={schedule_on_event_key}
      schedule_send_at={schedule_send_at}
      isSurveyCompleted={!!trigger_survey_id}
    />
  );
};

export type NextSendsProps = {
  immediate: boolean;
  timezone: string | null;
  event_calendar: boolean;
  event_title: string | null;
  event_number_of_repeats?: number;
  event_repeat?: INTERVAL | null;
  event_date?: ISO8601_DATE;
  event_key: string | null;
  schedule_event_initial: boolean;
  schedule_event_repeat: boolean | null;
  schedule_interval_json: IntervalJson;
  schedule_on_event_key: string[] | null;
  schedule_send_at: ISO8601_TIME;
  isSurveyCompleted?: boolean;
};

export const NextSends: FC<NextSendsProps> = ({
  immediate,
  timezone,
  event_calendar,
  event_title,
  event_number_of_repeats,
  event_repeat,
  event_date,
  event_key,
  schedule_event_initial,
  schedule_event_repeat,
  schedule_interval_json,
  schedule_on_event_key,
  schedule_send_at,
  isSurveyCompleted,
}) => {
  const [exampleDate, setExampleDate] = useState<Date | null>(null);

  const { data, loading } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!event_key) return [];

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() + 1);
      if (event_key === "birthday") yesterday.setFullYear(yesterday.getFullYear() - 1);
      setExampleDate(yesterday);
      const date = event_calendar ? event_date : toISO8601_DATE(yesterday);
      if (!date) return [];

      const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
      const supabaseScheduleService = new SupabaseScheduleService(supabase);
      const { data: whenData } = await supabaseScheduleService.getNextSends(
        date,
        schedule_event_initial,
        schedule_event_repeat ? (event_number_of_repeats ?? 0) : 0,
        schedule_event_repeat && event_repeat ? event_repeat : ("0 hours" as INTERVAL),
        schedule_interval_json,
        toDow(schedule_on_event_key ?? []),
        schedule_send_at,
        tz,
      );

      return whenData ?? [];
    },
    [
      event_calendar,
      event_date,
      event_key,
      event_number_of_repeats,
      event_repeat,
      schedule_event_initial,
      schedule_event_repeat,
      schedule_interval_json,
      schedule_on_event_key,
      schedule_send_at,
      timezone,
    ],
  );

  return (
    <Stack direction="column" spacing={1}>
      <Subtitle
        immediate={immediate}
        nextSends={data ?? []}
        isSurveyCompleted={isSurveyCompleted}
      />
      {immediate || (data ?? []).length === 0 ? null : (
        <Box>
          {loading ? (
            <LinearProgress />
          ) : (
            <>
              <ul data-testid="next-sends-list">
                {(data ?? []).map((w) => (
                  <li key={w.when}>
                    <Typography variant="body2">
                      {formatDatetime(new Date(w.when), undefined)}
                    </Typography>
                  </li>
                ))}
              </ul>
              {timezone && <Timezone timezone={timezone} />}
              {!event_calendar && (
                <Example
                  eventTitle={
                    event_key && isSurveyCompletedKey(event_key)
                      ? `${event_title ?? ""} completed`.trim()
                      : event_title
                  }
                  exampleDate={exampleDate}
                />
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  );
};

const Example: FC<{ eventTitle: string | null; exampleDate: Date | null }> = ({
  eventTitle,
  exampleDate,
}) => {
  const formattedDate = exampleDate
    ? exampleDate.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
      })
    : "_";

  return (
    <Typography variant="subtitle2">
      For an example{" "}
      <Box component="span" sx={{ fontStyle: "italic" }}>
        {eventTitle ? capitalizeEachWord(eventTitle) : "_"}
      </Box>
      {` event on ${formattedDate}`}
    </Typography>
  );
};

const Subtitle: FC<{
  immediate: boolean;
  nextSends: ScheduleWhen[] | "loading";
  isSurveyCompleted?: boolean;
}> = ({ immediate, nextSends, isSurveyCompleted }) => {
  let subtitle;
  if (immediate) {
    subtitle = "This Communication will send immediately upon publishing";
  } else if (isSurveyCompleted) {
    subtitle = "This Communication will send each time someone completes the selected survey";
  } else if (nextSends === "loading" || nextSends.length === 1) {
    subtitle = "This Communication will send at";
  } else if (nextSends.length === 0) {
    subtitle = "We’ll show you a schedule preview as you time your Communication";
  } else {
    subtitle = "The first five Communications will send at";
  }

  return <Typography variant="subtitle2">{subtitle}</Typography>;
};

const Timezone: FC<{ timezone: string }> = ({ timezone }) => {
  return <Typography variant="subtitle2">{`In time zone ${timezone}`}</Typography>;
};
